import React from 'react';

import {
  BonusCardStatus,
  BonusCardTimer,
  BonusCardType,
  BonusTitle,
  resolveBonusCardStatus,
} from '@atlas/components';
import { SiteConfigManager } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { BonusDetailsHeaderMobileSkeleton, Icon, Label, Timer } from '@atlas/ui';
import useFormatter from 'src/hooks/useFormatter';

import { useBonusDetailContext } from './BonusDetailContext';
import BonusDetailHeaderActions from './BonusDetailHeaderActions';

const { SPORT_PREMATCH } = COMPONENT_TYPES;

/**
 * The Casino/Sport product badge is hidden on casino-only projects (no active
 * sportsbook page in the navigation config) — there is nothing to distinguish.
 */
const isCasinoOnlyProject = () => {
  const pages = SiteConfigManager.getConfig('config.pages', []) || [];
  return !pages.some(({ isActive, type }) => isActive && type === SPORT_PREMATCH);
};

const Skeleton = () => {
  return (
    <section className="panel">
      <BonusDetailsHeaderMobileSkeleton />
    </section>
  );
};

// Freespins are granted as a spin COUNT, not a currency amount -> "{count} FS".
const FREESPIN_UNIT = 'FS';

/**
 * Badge shown in the header, by status group (UI spec):
 *   Available -> deposit / reload-deposit: 'Min Deposit: [amount]'
 *               -> no-deposit:               'Bonus [amount]'
 *   Unlocked  -> 'Bonus [amount]'
 *   Activated -> 'Bonus Received [amount]'
 *   Finished  -> none
 * Freespins (outside available deposit) carry their spin count instead of a
 * currency amount -> 'Bonus Received: {count} FS' (mirrors the list card).
 * Returns null when there is no positive value to display. The value is rendered
 * in a separate span (currency-formatted, or "{count} FS" when isCount).
 */
const resolveHeaderMeta = (actionGroup, bonusInfo, isDepositLike, isFreeSpin) => {
  // Available deposit / reload-deposit (incl. deposit freespins) -> Min Deposit.
  if (actionGroup === 'available' && isDepositLike) {
    return bonusInfo?.minDeposit > 0
      ? {
          amount: bonusInfo.minDeposit,
          className: 'bonus-details__header-meta-min-deposit',
          labelKey: 'bonuses_details_bonus_min_deposit',
        }
      : null;
  }

  // Freespins show the received spin count, e.g. "Bonus Received: 150 FS".
  if (isFreeSpin) {
    return bonusInfo?.count > 0
      ? {
          amount: bonusInfo.count,
          className: 'bonus-details__header-meta-bonus-received',
          isCount: true,
          labelKey: 'bonuses_details_bonus_received',
        }
      : null;
  }

  const configByGroup = {
    activated: {
      amount: bonusInfo?.bonusReceived,
      className: 'bonus-details__header-meta-bonus-received',
      labelKey: 'bonuses_details_bonus_received',
    },
    available: {
      amount: bonusInfo?.bonusAmount,
      className: 'bonus-details__header-meta-bonus',
      labelKey: 'bonuses_details_bonus',
    },
    unlocked: {
      amount: bonusInfo?.bonusAmount,
      className: 'bonus-details__header-meta-bonus',
      labelKey: 'bonuses_details_bonus',
    },
  };

  const config = configByGroup[actionGroup];
  return config && config.amount > 0 ? config : null;
};

const BonusDetailHeader = () => {
  const { entity, kind, loading } = useBonusDetailContext();
  const { currencyFormatter } = useFormatter(entity?.currency);
  const [showDefaultImage, setShowDefaultImage] = React.useState(false);

  const resolved = resolveBonusCardStatus(entity?.status);
  const isFreeSpin = kind === 'freespin';
  const isDepositLike = Boolean(entity?.isDepositLike);
  const headerMeta = resolveHeaderMeta(
    resolved.actionGroup,
    entity?.bonusInfo,
    isDepositLike,
    isFreeSpin,
  );
  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');
  const showProductBadge = !isCasinoOnlyProject() && !hideSportBonuses;

  if (loading) return <Skeleton />;

  return (
    <section className="bonus-details__header panel">
      <div className="bonus-details__header-meta">
        <BonusCardTimer
          IconComponent={Icon}
          expiryDate={
            entity?.timer?.endMs != null ? (
              <Timer time={entity.timer.endMs} variant="compact" />
            ) : (
              entity?.timer?.finishedDate
            )
          }
        />

        <div className="bonus-details__header-badges">
          {showProductBadge && <BonusCardType LabelComponent={Label} bonusType={entity.type} />}
          <BonusCardStatus
            LabelComponent={Label}
            labelKey={resolved.labelKey}
            statusClass={resolved.statusClass}
          />
        </div>
      </div>

      <div className="bonus-details__header-body">
        <div className="bonus-details__header-content">
          <BonusTitle
            className="bonus-details__header-title"
            name={entity?.name}
            nameHighlighted={entity?.nameHighlighted}
          />

          {headerMeta && (
            <p className={headerMeta.className}>
              <Label message={headerMeta.labelKey} />
              <span className="min-deposit-semibold-text">
                {headerMeta.isCount
                  ? `${headerMeta.amount} ${FREESPIN_UNIT}`
                  : currencyFormatter.format(headerMeta.amount)}
              </span>
            </p>
          )}
        </div>

        {!entity?.imageUrl || showDefaultImage ? (
          <Icon className="bonus-details__header-image" height={102} name="bonus" width={102} />
        ) : (
          <img
            alt="Bonus"
            className="bonus-details__header-image"
            onError={() => setShowDefaultImage(true)}
            src={entity?.imageUrl}
          />
        )}
      </div>

      <BonusDetailHeaderActions
        actionGroup={resolved.actionGroup}
        isDepositLike={isDepositLike}
        isFreeSpin={isFreeSpin}
      />
    </section>
  );
};

export default BonusDetailHeader;
